import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { backendService } from "../../../services/backend.service";
import { quantizeTrainingOrientationWage, type TrainingOrientationWageBody } from "../../../utils/DepartmentWage.config";
import type { WageSettingsSection } from "./useWageRulesConfig";
import { updateWageSettingsCache } from "./useWageSettings";

/** Same events as the rule tables' saves, told apart by `section`. */
const TRAINING_ORIENTATION_SECTION: WageSettingsSection = "training_orientation";

/**
 * Resolves to the backend's `changed` flag. A change always queues a recalculation of every shift, past
 * and future, so it is also what picks the "being recalculated" message over a plain "updated".
 *
 * No refetch: the success path writes the stored (quantized) pair into the tab's own read, which the
 * section's baseline comes from, AND into `chosenDepartment`, so other screens reading it stay consistent.
 */
export const useSaveTrainingOrientationWage = () => {
	const { t } = useTranslation();
	const { chosenDepartment, setChosenDepartment, showErrorSnackbar } = useHospitalData();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();
	const queryClient = useQueryClient();
	const departmentId = chosenDepartment.department_id;

	return useMutation<boolean, Error, TrainingOrientationWageBody>({
		mutationFn: async (body) => {
			const response = await backendService.setTrainingOrientationWage(departmentId, body);
			return response.data.changed;
		},
		onMutate: (body) => {
			track("team_wage_settings_save_start", {
				department_id: departmentId,
				section: TRAINING_ORIENTATION_SECTION,
				orientation_wage_type: body.orientationWageType,
				training_wage_type: body.trainingWageType,
			});
		},
		onSuccess: (changed, body) => {
			const stored = quantizeTrainingOrientationWage(body);
			updateWageSettingsCache(queryClient, departmentId, (settings) => ({
				...settings,
				departmentWageConfig: { ...settings.departmentWageConfig, ...stored },
			}));
			setChosenDepartment((prev) => ({ ...prev, department_wage_config: { ...prev.department_wage_config, ...stored } }));
			track("team_wage_settings_save_success", { department_id: departmentId, section: TRAINING_ORIENTATION_SECTION, changed });
			showSnackbar(t(changed ? "team.wageSettings.saveSuccessRecalculating" : "team.wageSettings.saveSuccess"), "success");
		},
		onError: (error) => {
			trackError("team_wage_settings_save_error", { department_id: departmentId, section: TRAINING_ORIENTATION_SECTION, error: error.message });
			// showErrorSnackbar unwraps and translates the backend's UIVisibleError key; this string is
			// only the fallback for a non-UIVisibleError failure.
			showErrorSnackbar(t("team.wageSettings.saveError"), {}, error);
		},
		throwOnError: false,
	});
};
